import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell,
} from 'recharts';
import { API } from './App';

// ── Date helpers ──────────────────────────────────────────────────────────────

function localISO(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function today() { return localISO(new Date()); }

function daysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return localISO(d);
}

function monthsAgo(n) {
  const d = new Date();
  d.setMonth(d.getMonth() - n, 1);
  return localISO(d);
}

const PRESETS = [
  { key: '30d',    label: '30 Days',  start: () => daysAgo(29),   end: today, by: 'day'   },
  { key: '12w',    label: '12 Weeks', start: () => daysAgo(83),   end: today, by: 'week'  },
  { key: '12m',    label: '12 Months',start: () => monthsAgo(11), end: today, by: 'month' },
  { key: 'custom', label: 'Custom',   start: null, end: null,      by: null   },
];

// ── Fill gaps in sparse API data ─────────────────────────────────────────────

function fillGaps(data, startDate, endDate, by) {
  const map = Object.fromEntries(data.map(r => [r.period, r]));
  const result = [];
  const s = new Date(startDate + 'T00:00:00');
  const e = new Date(endDate   + 'T00:00:00');

  if (by === 'day') {
    for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
      const k = localISO(d);
      result.push(map[k] || { period: k, revenue: 0, count: 0 });
    }
  } else if (by === 'week') {
    // Align to Monday on or before startDate
    const dow = s.getDay();
    const monday = new Date(s);
    monday.setDate(s.getDate() - (dow === 0 ? 6 : dow - 1));
    for (let d = new Date(monday); d <= e; d.setDate(d.getDate() + 7)) {
      const k = localISO(d);
      result.push(map[k] || { period: k, revenue: 0, count: 0 });
    }
  } else {
    // month
    let yr = s.getFullYear(), mo = s.getMonth() + 1;
    const endYr = e.getFullYear(), endMo = e.getMonth() + 1;
    while (yr < endYr || (yr === endYr && mo <= endMo)) {
      const k = `${yr}-${String(mo).padStart(2, '0')}`;
      result.push(map[k] || { period: k, revenue: 0, count: 0 });
      if (++mo > 12) { mo = 1; yr++; }
    }
  }
  return result;
}

// ── Format helpers ────────────────────────────────────────────────────────────

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function labelFor(period, by) {
  if (by === 'month') {
    const [yr, mo] = period.split('-');
    return `${MONTHS[parseInt(mo) - 1]} '${yr.slice(2)}`;
  }
  const d = new Date(period + 'T00:00:00');
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

function fmtMoney(v) {
  if (v >= 100000) return `$${(v / 1000).toFixed(0)}k`;
  if (v >= 1000)   return `$${(v / 1000).toFixed(1)}k`;
  return `$${Number(v).toFixed(0)}`;
}

// ── CSV export ────────────────────────────────────────────────────────────────

function downloadCSV(data, by, startDate, endDate) {
  const byLabel = { day: 'Date', week: 'Week Starting', month: 'Month' }[by];
  const header = `${byLabel},Revenue,Payments\n`;
  const rows = data
    .map(r => `${r.period},$${r.revenue.toFixed(2)},${r.count}`)
    .join('\n');
  const blob = new Blob([header + rows], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `revenue_${startDate}_${endDate}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Shared chart/table styles ─────────────────────────────────────────────────

const AXIS  = { tick: { fill: '#555', fontSize: 10 }, axisLine: false, tickLine: false };
const GRID  = { strokeDasharray: '3 3', stroke: '#1a1a1a', vertical: false };
const TIP   = {
  contentStyle: { background: '#161616', border: '0.5px solid #2a2a2a', borderRadius: 8, color: '#f0ede8', fontSize: 12 },
  cursor: { fill: '#1e1e1e' },
};

// ── Stat card ─────────────────────────────────────────────────────────────────

function Stat({ label, value, sub }) {
  return (
    <div style={{ background: '#161616', border: '0.5px solid #2a2a2a', borderRadius: 10, padding: 16 }}>
      <div style={{ fontSize: 11, color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.5px', color: '#f0ede8' }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: '#555', marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function Reports({ getToken }) {
  const [preset, setPreset]           = useState('30d');
  const [customStart, setCustomStart] = useState(daysAgo(29));
  const [customEnd,   setCustomEnd]   = useState(today());
  const [customBy,    setCustomBy]    = useState('day');

  const [result,  setResult]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  // Derive active filter params from preset or custom inputs
  const activeParams = useCallback(() => {
    if (preset !== 'custom') {
      const p = PRESETS.find(p => p.key === preset);
      return { start: p.start(), end: p.end(), by: p.by };
    }
    return { start: customStart, end: customEnd, by: customBy };
  }, [preset, customStart, customEnd, customBy]);

  // Fetch on filter change
  useEffect(() => {
    const { start, end, by } = activeParams();
    if (!start || !end || start > end) return;

    setLoading(true);
    setError(null);

    getToken()
      .then(token =>
        fetch(`${API}/api/reports?start=${start}&end=${end}&by=${by}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
      )
      .then(async r => {
        if (!r.ok) {
          const b = await r.json().catch(() => ({}));
          throw new Error(b.error || `HTTP ${r.status}`);
        }
        return r.json();
      })
      .then(d => { setResult(d); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [getToken, activeParams]);

  // Compute filled data for the chart
  const filled = result
    ? fillGaps(result.data, result.startDate, result.endDate, result.by)
    : [];

  const { start, end, by } = activeParams();
  const totalRevenue = result?.totalRevenue || 0;
  const totalCount   = result?.totalCount   || 0;
  const avg          = totalCount ? totalRevenue / totalCount : 0;

  // ── Styles ──

  const inputStyle = {
    background: '#161616', border: '0.5px solid #2a2a2a', borderRadius: 6,
    color: '#f0ede8', fontSize: 12, padding: '6px 10px',
    fontFamily: "'DM Sans', sans-serif", outline: 'none',
  };
  const presetBtn = (key) => ({
    background: preset === key ? '#242424' : 'none',
    border: preset === key ? '0.5px solid #3a3a3a' : '0.5px solid #2a2a2a',
    color: preset === key ? '#f0ede8' : '#666',
    borderRadius: 6, fontSize: 12, padding: '5px 14px',
    fontFamily: "'DM Sans', sans-serif", cursor: 'pointer', transition: 'all 0.15s',
  });

  return (
    <div>
      {/* ── Filter bar ── */}
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
        {/* Preset buttons */}
        <div style={{ display: 'flex', gap: 4 }}>
          {PRESETS.map(p => (
            <button key={p.key} onClick={() => setPreset(p.key)} style={presetBtn(p.key)}>
              {p.label}
            </button>
          ))}
        </div>

        {/* Custom controls */}
        {preset === 'custom' && (
          <>
            <span style={{ fontSize: 11, color: '#555' }}>From</span>
            <input
              type="date" value={customStart} max={customEnd}
              onChange={e => setCustomStart(e.target.value)}
              style={inputStyle}
            />
            <span style={{ fontSize: 11, color: '#555' }}>To</span>
            <input
              type="date" value={customEnd} min={customStart} max={today()}
              onChange={e => setCustomEnd(e.target.value)}
              style={inputStyle}
            />
            <select
              value={customBy} onChange={e => setCustomBy(e.target.value)}
              style={{ ...inputStyle, cursor: 'pointer' }}
            >
              <option value="day">By Day</option>
              <option value="week">By Week</option>
              <option value="month">By Month</option>
            </select>
          </>
        )}

        {/* Spacer + Export */}
        <div style={{ marginLeft: 'auto' }}>
          <button
            onClick={() => result && downloadCSV(result.data, result.by, result.startDate, result.endDate)}
            disabled={!result || result.data.length === 0}
            style={{
              background: '#1a3a2a', border: '0.5px solid #2a5a3a', color: '#4ade80',
              borderRadius: 6, fontSize: 12, padding: '5px 14px',
              fontFamily: "'DM Sans', sans-serif", cursor: 'pointer',
              opacity: (!result || result.data.length === 0) ? 0.4 : 1,
            }}
          >
            ↓ Export CSV
          </button>
        </div>
      </div>

      {/* ── States ── */}
      {loading && <div style={{ color: '#555', fontSize: 13, padding: '24px 0' }}>Loading...</div>}
      {error   && <div style={{ color: '#f87171', fontSize: 13, padding: '16px 0' }}>{error}</div>}

      {!loading && !error && result && (
        <>
          {/* ── Summary stats ── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12, marginBottom: 24 }}>
            <Stat label="Total Revenue"    value={`$${totalRevenue.toFixed(2)}`} sub={`${start} → ${end}`} />
            <Stat label="Total Payments"   value={totalCount}                    sub={`${by === 'day' ? 'daily' : by === 'week' ? 'weekly' : 'monthly'} view`} />
            <Stat label="Avg per Payment"  value={`$${avg.toFixed(2)}`}          sub={totalCount ? `across ${totalCount} transactions` : 'no data'} />
          </div>

          {/* ── Bar chart ── */}
          <div style={{ background: '#161616', border: '0.5px solid #2a2a2a', borderRadius: 10, padding: '20px 8px 8px', marginBottom: 24 }}>
            {filled.length === 0 ? (
              <div style={{ color: '#555', fontSize: 13, textAlign: 'center', padding: 40 }}>No payments in this period.</div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={filled} margin={{ top: 0, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid {...GRID} />
                  <XAxis
                    dataKey="period"
                    tickFormatter={v => labelFor(v, by)}
                    {...AXIS}
                    interval="preserveStartEnd"
                  />
                  <YAxis tickFormatter={fmtMoney} {...AXIS} width={52} />
                  <Tooltip
                    {...TIP}
                    formatter={v => [`$${Number(v).toFixed(2)}`, 'Revenue']}
                    labelFormatter={v => labelFor(v, by)}
                  />
                  <Bar dataKey="revenue" radius={[3, 3, 0, 0]} maxBarSize={40}>
                    {filled.map((_, i) => (
                      <Cell key={i} fill={i === filled.length - 1 ? '#4ade80' : '#1a3a2a'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* ── Data table ── */}
          {filled.some(r => r.revenue > 0) && (
            <div>
              <div style={{ fontSize: 11, color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>
                Breakdown
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['Period', 'Revenue', 'Payments', 'Avg per Payment'].map(h => (
                      <th key={h} style={{ fontSize: 11, color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '8px 12px', textAlign: 'left', borderBottom: '0.5px solid #1e1e1e' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[...filled].reverse().filter(r => r.revenue > 0 || r.count > 0).map(r => (
                    <tr key={r.period} style={{ borderBottom: '0.5px solid #1a1a1a' }}>
                      <td style={{ padding: 12, fontSize: 13, color: '#f0ede8', fontFamily: "'DM Mono', monospace" }}>
                        {labelFor(r.period, by)}
                      </td>
                      <td style={{ padding: 12, fontSize: 13, color: '#4ade80', fontFamily: "'DM Mono', monospace", fontWeight: 500 }}>
                        ${r.revenue.toFixed(2)}
                      </td>
                      <td style={{ padding: 12, fontSize: 13, color: '#888' }}>
                        {r.count}
                      </td>
                      <td style={{ padding: 12, fontSize: 13, color: '#888', fontFamily: "'DM Mono', monospace" }}>
                        {r.count ? `$${(r.revenue / r.count).toFixed(2)}` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
